import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Avatar, Badge, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { hubForPath, visibleHubs, defaultPath, NavPerms, FALLBACK_MODULES } from '../../config/navHubs';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';
import { useNotifications } from '../../contexts/NotificationContext';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { accentOn } from '../../utils/contrast';
import logoMark from '../../assets/branding/logo-mark.svg';
import {
  Home,
  School,
  Analytics,
  EmojiEvents,
  Forum,
  Assignment,
  AdminPanelSettings,
  Mood as MoodIcon,
  Spa,
  Logout,
  BeachAccess,
  Assessment,
  PersonOutline,
  SettingsOutlined,
  SmartToy,
} from '@mui/icons-material';

const RAIL_WIDTH = 80;

const RAIL_STYLES = {
  navSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingBottom: '8px',
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '2px' },
    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.3)' },
  },
  navItemsGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '8px',
  },
};

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
  managerOnly?: boolean;
  superAdminOnly?: boolean;
  moduleName?: string;
}

// Hub id → rail icon.
const HUB_ICONS: Record<string, React.ReactNode> = {
  home: <Home />, grow: <School />, wellbeing: <Spa />, performance: <Assessment />,
  rewards: <EmojiEvents />, workspace: <Assignment />, connect: <Forum />, leave: <BeachAccess />,
};
// Which legacy per-item badge counts roll up onto which hub.
const HUB_BADGE_IDS: Record<string, string[]> = {
  dashboard: ['dashboard'], grow: ['learning', 'games'], connect: ['announcements', 'chat'],
  rewards: ['store'], workspace: ['tasks'],
};

// Single Admin Hub link instead of extended menu
const adminItems: NavItem[] = [
  { id: 'insights', label: 'Insights', icon: <Analytics />, path: '/insights', adminOnly: true },
  { id: 'admin-hub', label: 'Admin Hub', icon: <AdminPanelSettings />, path: '/admin', adminOnly: true },
  // A super admin's rail has no hubs, so MoreMe-wide pages need their own entries.
  { id: 'mood-analytics', label: 'Mood analytics', icon: <MoodIcon />, path: '/admin/mood-analytics', superAdminOnly: true },
  { id: 'moreme-courses', label: 'MoreMe courses', icon: <School />, path: '/admin/moreme-courses', superAdminOnly: true },
];

function NavigationRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  // The account menu. Personal Settings (including Connect an AI assistant) was only reachable from the
  // phone top bar; on desktop the avatar went straight to Profile and nothing linked to Settings.
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const goAccount = (path: string) => { setAccountAnchor(null); navigate(path); };
  const [enabledModules, setEnabledModules] = useState<string[]>(FALLBACK_MODULES);
  const [cultureScheduledToday, setCultureScheduledToday] = useState(false);
  // Counts are polled once, in NotificationContext.
  const { counts: notificationCounts } = useNotifications();

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await apiClient.get('/company/modules', { skipErrorToast: true });
        if (response.data?.success && response.data?.data) {
          const activeModules = response.data.data
            .filter((m: { isActive: boolean }) => m.isActive)
            .map((m: { moduleName: string }) => m.moduleName);
          if (activeModules.length) setEnabledModules(activeModules);
        }
      } catch (error) {
        // Keep FALLBACK_MODULES — never blank the nav on a failed fetch.
      }
    };
    fetchModules();
    apiClient.get('/culture/is-scheduled-today', { skipErrorToast: true }).then(res => {
      if (res.data?.success && res.data?.data?.isScheduled) setCultureScheduledToday(true);
    }).catch(() => {});
  }, []);

  const isAdmin = user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isManager = user && ['manager', 'lead', 'hr', 'admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';

  // Check if a module is enabled

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get badge count for a nav item
  const getBadgeCount = (itemId: string): number => {
    switch (itemId) {
      case 'announcements':
        return notificationCounts.announcements;
      case 'policies':
        return (notificationCounts as any).policies || 0;
      case 'learning':
      case 'microlearning':
        return notificationCounts.learning;
      case 'games':
        return notificationCounts.games;
      case 'store':
        return notificationCounts.store || 0;
      case 'chat':
      case 'pulse':
        return notificationCounts.chat || 0;
      case 'tasks':
        return notificationCounts.tasks;
      default:
        return 0;
    }
  };

  const sidebarBg = branding.sidebarColor || '#1A1D1F';
  const sidebarText = branding.sidebarTextColor || '#FFFFFF';
  const primaryColor = branding.primaryColor || '#2D5BE3';
  // The active item is drawn in the brand primary on the sidebar colour. A
  // tenant whose sidebar is painted in (or near) its own primary would get a
  // blue-on-blue active item that vanishes the moment it's clicked, so fall
  // back to the sidebar's text colour when primary doesn't stand out.
  const activeColor = accentOn(primaryColor, sidebarBg, sidebarText);

  return (
    <Box sx={{
      width: RAIL_WIDTH,
      height: '100vh',
      background: sidebarBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px 0',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1200,
    }}>
      <Tooltip title={branding.companyName || 'MoreMe AI'} placement="right">
        <Box
          onClick={() => navigate(isSuperSuperAdmin ? '/admin/super' : '/dashboard')}
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: branding.logoUrl ? 'transparent' : primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {branding.logoUrl ? (
            <Box
              component="img"
              src={resolveAssetUrl(branding.logoUrl)}
              alt={branding.companyName || 'Logo'}
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Box
              component="img"
              src={logoMark}
              alt="MoreMe AI"
              sx={{ width: 28, height: 28, filter: 'brightness(10)' }}
            />
          )}
        </Box>
      </Tooltip>

      <Box sx={RAIL_STYLES.navSection}>
        {!isSuperSuperAdmin && (
        <Box sx={RAIL_STYLES.navItemsGroup}>
            {(() => {
              const navPerms: NavPerms = { enabledModules, isManager: !!isManager, isAdmin: !!isAdmin, isSuperAdmin: user?.role === 'super-super-admin' };
              const activeHub = hubForPath(location.pathname);
              return visibleHubs(navPerms).map((hub) => {
                const target = defaultPath(hub, navPerms);
                const active = activeHub?.id === hub.id;
                const badgeCount = (HUB_BADGE_IDS[hub.id] || []).reduce((s, id) => s + getBadgeCount(id), 0);
                const cultureCue = hub.id === 'connect' && cultureScheduledToday;
                return (
                  <Tooltip key={hub.id} title={hub.label} placement="right">
                    <span>
                      <IconButton
                        onClick={() => target && navigate(target)}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          color: active ? activeColor : `${sidebarText}99`,
                          backgroundColor: active ? `${activeColor}20` : 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: active ? `${activeColor}30` : `${sidebarText}15`,
                            color: active ? activeColor : sidebarText,
                          },
                          '& svg': { fontSize: 24 },
                        }}
                      >
                        {(badgeCount > 0 || cultureCue) ? (
                          <Badge
                            badgeContent={cultureCue && badgeCount === 0 ? '!' : (badgeCount > 99 ? '99+' : badgeCount)}
                            max={99}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: cultureCue ? (branding.secondaryColor || '#8B5CF6') : '#EF4444',
                                color: 'white', fontSize: '10px', fontWeight: 700,
                                minWidth: '18px', height: '18px', borderRadius: '9px',
                                padding: '0 4px', border: `2px solid ${sidebarBg}`,
                              },
                            }}
                          >
                            {HUB_ICONS[hub.icon]}
                          </Badge>
                        ) : HUB_ICONS[hub.icon]}
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              });
            })()}
        </Box>
        )}

        {isAdmin && (
          <Box sx={{ ...RAIL_STYLES.navItemsGroup, pt: 2, borderTop: `1px solid ${sidebarText}1A`, mt: 2 }}>
            {adminItems.filter((item) => !item.superAdminOnly || isSuperSuperAdmin).map((item) => {
              const superAdminHub = item.id === 'admin-hub' && isSuperSuperAdmin;
              return (
              <Tooltip key={item.id} title={superAdminHub ? 'Super Admin Dashboard' : item.label} placement="right">
                <IconButton
                  onClick={() => navigate(superAdminHub ? '/admin/super' : item.path)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    color: isActive(item.path) ? activeColor : `${sidebarText}99`,
                    backgroundColor: isActive(item.path) ? `${activeColor}20` : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isActive(item.path) ? `${activeColor}30` : `${sidebarText}15`,
                      color: isActive(item.path) ? activeColor : sidebarText,
                    },
                    '& svg': { fontSize: 24 },
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        paddingTop: '16px',
        borderTop: `1px solid ${sidebarText}1A`,
        marginTop: 'auto',
      }}>
        {isSuperSuperAdmin ? (
          <>
          <Tooltip title="Settings" placement="right">
            <IconButton
              onClick={() => navigate('/settings')}
              aria-label="Settings"
              sx={{ width: 48, height: 48, borderRadius: '12px', color: `${sidebarText}99`, '&:hover': { backgroundColor: `${sidebarText}15`, color: sidebarText }, '& svg': { fontSize: 24 } }}
            >
              <SettingsOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout" placement="right">
            <IconButton
              onClick={logout}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                color: `${sidebarText}99`,
                '&:hover': { backgroundColor: `${sidebarText}15`, color: 'var(--color-error)' },
                '& svg': { fontSize: 24 },
              }}
            >
              <Logout />
            </IconButton>
          </Tooltip>
          </>
        ) : (
        <Tooltip title={`${user?.firstName} ${user?.lastName}`} placement="right">
          <Avatar
            src={user?.avatar}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              border: `2px solid ${sidebarText}40`,
              background: primaryColor,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: sidebarBg,
            }}
            onClick={(e) => setAccountAnchor(e.currentTarget)}
            aria-label="Account menu"
            role="button"
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
        </Tooltip>
        )}
        <Menu
          anchorEl={accountAnchor}
          open={Boolean(accountAnchor)}
          onClose={() => setAccountAnchor(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MenuItem onClick={() => goAccount('/profile')}>
            <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => goAccount('/settings')}>
            <ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={() => goAccount('/settings?tab=ai')}>
            <ListItemIcon><SmartToy fontSize="small" /></ListItemIcon>
            Connect an AI assistant
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setAccountAnchor(null); logout(); }}>
            <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}

export default NavigationRail;
